/**
 * CartePanel — the fidélité card view. Settle a sale's accrual (POST .../cartes/:id/accrual) or
 * reverse points (POST .../cartes/:id/annuler) — both cap finance.fidelite.manage. Points accrue on
 * NET spend and PROMOTE the palier (bronze → argent → or); points are NEVER redeemed for cash.
 *
 * There is no GET-card endpoint, so the "card" view is the latest accrual/reversal result echoed
 * read-only (solde + palier + promotion). Both actions are gated by useCan; the server re-checks.
 */
import { useCallback, useState, type FormEvent } from "react";
import { Badge, Button, Card, FormField, Input, Modal, Textarea, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { token } from "@/design/theme";
import { FINANCE_CAPS, useAccrual, useReverse } from "../api";
import type { AccrualResult } from "../types";
import { MoneyInput } from "./MoneyInput";

interface CardState {
  carteId: string;
  solde_points: number;
  palier: string;
  lastGain?: number | undefined;
  promoted?: boolean | undefined;
}

const PALIER_TONE: Record<string, "neutral" | "warn" | "success" | "info"> = {
  bronze: "neutral",
  argent: "info",
  or: "warn",
};

export function CartePanel() {
  const { t } = useT();
  const toast = useToast();
  const canManage = useCan(FINANCE_CAPS.fideliteManage);

  const accrualMut = useAccrual();
  const reverseMut = useReverse();

  const [carteId, setCarteId] = useState("");
  const [netPaid, setNetPaid] = useState<number | null>(null);
  const [factureId, setFactureId] = useState("");
  const [touched, setTouched] = useState(false);
  const [card, setCard] = useState<CardState | null>(null);

  const [reverseOpen, setReverseOpen] = useState(false);
  const [reversePoints, setReversePoints] = useState("");
  const [reverseNote, setReverseNote] = useState("");
  const [reverseTouched, setReverseTouched] = useState(false);

  const carteError = touched && carteId.trim() === "" ? t("finance.field.required") : null;
  const netError = touched && (netPaid == null || netPaid <= 0) ? t("finance.field.amountInvalid") : null;
  const valid = carteId.trim() !== "" && netPaid != null && netPaid > 0;

  const submitAccrual = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canManage) return;
    accrualMut.mutate(
      {
        carteId: carteId.trim(),
        net_paid_centimes: netPaid ?? 0,
        ...(factureId.trim() ? { facture_id: factureId.trim() } : {}),
      },
      {
        onSuccess: (data: AccrualResult) => {
          toast.success(t("finance.fid.accrual.success"));
          setCard({
            carteId: carteId.trim(),
            solde_points: data.solde_apres,
            palier: data.palier,
            lastGain: data.gain,
            promoted: data.promoted,
          });
          setNetPaid(null);
          setFactureId("");
          setTouched(false);
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.fid.accrual.title"));
        },
      },
    );
  };

  const reversePointsValue = reversePoints.trim() === "" ? null : Number(reversePoints.replace(/\D/g, ""));
  const reverseInvalid =
    reversePointsValue == null || !Number.isInteger(reversePointsValue) || reversePointsValue <= 0;
  const reverseError = reverseTouched && reverseInvalid ? t("finance.field.intInvalid") : null;

  const submitReverse = (e: FormEvent) => {
    e.preventDefault();
    setReverseTouched(true);
    if (reverseInvalid || !canManage || !card) return;
    reverseMut.mutate(
      {
        carteId: card.carteId,
        points: reversePointsValue ?? 0,
        ...(reverseNote.trim() ? { note: reverseNote.trim() } : {}),
      },
      {
        onSuccess: (data) => {
          toast.success(t("finance.fid.reverse.success"));
          setCard((prev) =>
            prev
              ? { ...prev, solde_points: data.solde_apres, palier: data.palier, lastGain: undefined, promoted: false }
              : prev,
          );
          setReverseOpen(false);
          setReversePoints("");
          setReverseNote("");
          setReverseTouched(false);
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.fid.reverse.title"));
        },
      },
    );
  };

  const tone = card ? (PALIER_TONE[card.palier] ?? "neutral") : "neutral";

  // Stable close handler so the Modal focus-trap doesn't steal focus from the points field.
  const closeReverse = useCallback(() => {
    setReverseOpen(false);
    setReversePoints("");
    setReverseNote("");
    setReverseTouched(false);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <Card title={t("finance.fid.accrual.title")}>
        <form onSubmit={submitAccrual} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)", maxWidth: 480 }}>
            <FormField
              label={t("finance.fid.carte.id")}
              hint={t("finance.fid.carte.id.hint")}
              required
              {...(carteError ? { error: carteError } : {})}
            >
              {(p) => (
                <Input
                  {...p}
                  mono
                  className="nv-ltr-island"
                  inputMode="numeric"
                  autoComplete="off"
                  value={carteId}
                  invalid={!!carteError}
                  onChange={(e) => setCarteId(e.target.value)}
                />
              )}
            </FormField>

            <FormField
              label={t("finance.fid.accrual.netPaid")}
              hint={t("finance.fid.accrual.netPaid.hint")}
              required
              {...(netError ? { error: netError } : {})}
            >
              {(p) => (
                <MoneyInput
                  {...p}
                  aria-label={t("finance.amount.aria")}
                  valueCentimes={netPaid}
                  onChangeCentimes={setNetPaid}
                  invalid={!!netError}
                />
              )}
            </FormField>

            <FormField
              label={t("finance.fid.accrual.factureId")}
              hint={t("finance.fid.accrual.factureId.hint")}
            >
              {(p) => (
                <Input
                  {...p}
                  mono
                  className="nv-ltr-island"
                  autoComplete="off"
                  value={factureId}
                  onChange={(e) => setFactureId(e.target.value)}
                />
              )}
            </FormField>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="submit" loading={accrualMut.isPending} disabled={!canManage}>
                {t("finance.fid.accrual.submit")}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {card && (
        <Card
          title={t("finance.fid.solde.title")}
          actions={
            <div style={{ display: "flex", gap: "var(--nv-s-2)", alignItems: "center" }}>
              <Badge tone={tone}>{card.palier}</Badge>
              {card.promoted && <Badge tone="success">{t("finance.fid.accrual.promoted")}</Badge>}
            </div>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "var(--nv-s-3)",
                padding: "var(--nv-s-4)",
                borderRadius: "var(--nv-r-md)",
                background: token.surface2,
              }}
            >
              <span
                className="nv-mono nv-ltr-island"
                style={{ fontSize: "var(--nv-fs-display)", fontWeight: 600, color: token.ink }}
              >
                {card.solde_points}
              </span>
              <span style={{ color: token.slate }}>{t("finance.fid.accrual.soldeApres")}</span>
            </div>

            <dl style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "var(--nv-s-2)", margin: 0 }}>
              <dt style={{ color: token.slate }}>{t("finance.fid.carte.id")}</dt>
              <dd className="nv-mono nv-ltr-island" style={{ margin: 0, textAlign: "end" }}>
                {card.carteId}
              </dd>
              {card.lastGain != null && (
                <>
                  <dt style={{ color: token.slate }}>{t("finance.fid.accrual.gain")}</dt>
                  <dd className="nv-mono nv-ltr-island" style={{ margin: 0, textAlign: "end" }}>
                    +{card.lastGain}
                  </dd>
                </>
              )}
              <dt style={{ color: token.slate }}>{t("finance.fid.accrual.palier")}</dt>
              <dd style={{ margin: 0, textAlign: "end" }}>
                <Badge tone={tone}>{card.palier}</Badge>
              </dd>
            </dl>

            <div style={{ display: "flex", gap: "var(--nv-s-2)" }}>
              <Button
                variant="secondary"
                disabled={!canManage}
                onClick={() => {
                  setReverseOpen(true);
                  setReversePoints("");
                  setReverseNote("");
                  setReverseTouched(false);
                }}
              >
                {t("finance.fid.reverse.title")}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Modal
        open={reverseOpen}
        onClose={closeReverse}
        title={t("finance.fid.reverse.title")}
        footer={
          <>
            <Button variant="ghost" onClick={closeReverse} disabled={reverseMut.isPending}>
              {t("action.close")}
            </Button>
            <Button
              type="submit"
              form="finance-fid-reverse"
              variant="danger"
              loading={reverseMut.isPending}
              disabled={!canManage}
            >
              {t("finance.fid.reverse.submit")}
            </Button>
          </>
        }
      >
        <form id="finance-fid-reverse" onSubmit={submitReverse} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <p
              role="note"
              style={{
                margin: 0,
                padding: "var(--nv-s-3)",
                borderRadius: "var(--nv-r-md)",
                background: token.tintHigh,
                color: token.warn,
                fontSize: "var(--nv-fs-label)",
              }}
            >
              ▲ {t("finance.fid.reverse.warn")}
            </p>
            <FormField
              label={t("finance.fid.reverse.points")}
              hint={t("finance.fid.reverse.points.hint")}
              required
              {...(reverseError ? { error: reverseError } : {})}
            >
              {(p) => (
                <Input
                  {...p}
                  mono
                  className="nv-ltr-island"
                  inputMode="numeric"
                  autoComplete="off"
                  autoFocus
                  value={reversePoints}
                  invalid={!!reverseError}
                  style={{ maxWidth: 160, textAlign: "end" }}
                  onChange={(e) => setReversePoints(e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t("finance.fid.reverse.note")}>
              {(p) => (
                <Textarea
                  {...p}
                  rows={2}
                  value={reverseNote}
                  onChange={(e) => setReverseNote(e.target.value)}
                />
              )}
            </FormField>
          </div>
        </form>
      </Modal>
    </div>
  );
}
